import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Eye, EyeOff, Loader2, AlertCircle, Lock, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Default illustration for branded login
const DEFAULT_ILLUSTRATION = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80";

export default function BrandedLoginPage() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const { login, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [branding, setBranding] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBranding();
  }, [orgSlug]);

  useEffect(() => {
    // Redirect if already logged in
    if (user) {
      redirectBasedOnRole(user.role);
    }
  }, [user]);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      console.log('Fetching branding for slug:', orgSlug);
      const response = await axios.get(`${API}/api/public/org-branding/${orgSlug}`);
      console.log('Branding response:', response.data);
      setBranding(response.data);
    } catch (error) {
      console.error('Error fetching branding:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        // On other errors, still try to show the page with default branding
        toast.error('Failed to load organization branding');
      }
    } finally {
      setLoading(false);
    }
  };

  const redirectBasedOnRole = (role) => {
    switch (role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'organization':
        navigate('/organization');
        break;
      case 'agent':
        navigate('/agent');
        break;
      case 'student':
        navigate('/student');
        break;
      default:
        navigate('/');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/auth/login`, formData);
      const { access_token, user: userData } = response.data;
      login(access_token, userData);
      toast.success('Login successful!');
      redirectBasedOnRole(userData.role);
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      toast.error(message);
      setErrors({ general: message });
    } finally {
      setSubmitting(false);
    }
  };

  const brandColor = branding?.brand_color || '#3b82f6';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Organization Not Found</h1>
            <p className="text-slate-600 mb-6">
              The organization you're looking for doesn't exist or the URL is incorrect.
            </p>
            <Button onClick={() => navigate('/login')} className="bg-blue-500 hover:bg-blue-600">
              Go to Main Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Organization Logo */}
          <div className="mb-8 text-center">
            {branding?.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.organization_name}
                className="h-16 max-w-[200px] mx-auto object-contain"
              />
            ) : (
              <h1 
                className="text-2xl font-bold"
                style={{ color: brandColor }}
              >
                {branding?.organization_name || 'Organization'}
              </h1>
            )}
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome back</h2>
            <p className="text-slate-600">
              Sign in to access your account at {branding?.organization_name}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">
                Email
                {errors.email && (
                  <span className="text-red-500 text-sm ml-2">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    {errors.email}
                  </span>
                )}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`pl-10 h-12 ${errors.email ? 'border-red-500' : ''}`}
                  data-testid="branded-email-input"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Password
                  {errors.password && (
                    <span className="text-red-500 text-sm ml-2">
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      {errors.password}
                    </span>
                  )}
                </Label>
                <Link to="/forgot-password" className="text-sm hover:underline" style={{ color: branding.brand_color }}>
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`pl-10 pr-10 h-12 ${errors.password ? 'border-red-500' : ''}`}
                  data-testid="branded-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.general}
              </div>
            )}

            {/* Login Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 text-base font-medium"
              style={{ backgroundColor: brandColor }}
              data-testid="branded-login-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Log In'
              )}
            </Button>
          </form>

          {/* Footer - Powered By */}
          <div className="mt-12 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              Powered by{' '}
              <a 
                href="https://visainterviews.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-lime-600 hover:text-lime-700 font-medium"
              >
                VisaInterviews.ai
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Illustration */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12"
        style={{ backgroundColor: `${brandColor}10` }}
      >
        <div className="max-w-lg">
          <img
            src={DEFAULT_ILLUSTRATION}
            alt="Interview illustration"
            className="w-full h-auto"
          />
          <div className="text-center mt-8">
            <h3 className="text-xl font-semibold text-slate-800 mb-2">
              University Credibility Interview
            </h3>
            <p className="text-slate-600">
              Complete your interview securely from anywhere in the world
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
